import React, { useRef, useState } from "react";

import clsx from "clsx";

import {
  newElement,
  newElementWith,
  newTextElement,
} from "@excalidraw/element";

import { Popover } from "./Popover";
import { ToolButton } from "./ToolButton";

import type { AppClassProperties } from "../types";

const STICKY_NOTE_COLORS = [
  { name: "Yellow", stroke: "#f08c00", background: "#ffec99" },
  { name: "Blue", stroke: "#1971c2", background: "#a5d8ff" },
  { name: "Green", stroke: "#2f9e44", background: "#b2f2bb" },
  { name: "Red", stroke: "#e03131", background: "#ffc9c9" },
  { name: "Purple", stroke: "#9c36b5", background: "#da77f2" },
  { name: "Brown", stroke: "#846358", background: "#eaddd7" },
];

interface StickyNoteButtonProps {
  app: AppClassProperties;
  title?: string;
}

export const StickyNoteButton: React.FC<StickyNoteButtonProps> = ({
  app,
  title = "Sticky Note",
}) => {
  const [isOpen, setIsOpen] = useState(false);
  const buttonRef = useRef<HTMLButtonElement>(null);

  const createStickyNote = (color: typeof STICKY_NOTE_COLORS[0]) => {
    const width = 80;
    const height = 80;

    const stickyNote = newElement({
      type: "rectangle",
      x: 0,
      y: 0,
      width,
      height,
      strokeColor: color.stroke,
      backgroundColor: color.background,
      fillStyle: "solid",
      strokeWidth: 2,
      strokeStyle: "solid",
      roughness: 1,
      opacity: 100,
      roundness: null,
    });

    const textElement = newTextElement({
      x: 0,
      y: 0,
      fontSize: 8,
      text: " ",
      textAlign: "center",
      verticalAlign: "middle",
      containerId: stickyNote.id,
      strokeColor: "#000000",
    });

    const stickyNoteWithBinding = newElementWith(stickyNote, {
      boundElements: [{ id: textElement.id, type: "text" }],
    });

    app.onInsertElements([stickyNoteWithBinding, textElement]);
    setIsOpen(false);
  };

  return (
    <>
      <ToolButton
        ref={buttonRef}
        type="button"
        icon={
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <rect
              x="2"
              y="2"
              width="16"
              height="16"
              rx="1"
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
            />
            <line
              x1="2"
              y1="6"
              x2="18"
              y2="6"
              stroke="currentColor"
              strokeWidth="1.5"
              fill="none"
            />
          </svg>
        }
        title={title}
        aria-label={title}
        onClick={() => setIsOpen(!isOpen)}
        className={clsx("Shape", { active: isOpen })}
      />
      {isOpen && buttonRef.current && (
        <Popover
          onCloseRequest={() => setIsOpen(false)}
          top={buttonRef.current.offsetTop + buttonRef.current.offsetHeight + 4}
          left={buttonRef.current.offsetLeft}
          fitInViewport
        >
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(3, 1fr)",
              gap: "8px",
              padding: "8px",
            }}
          >
            {STICKY_NOTE_COLORS.map((color) => (
              <button
                key={color.name}
                type="button"
                onClick={() => createStickyNote(color)}
                style={{
                  width: "48px",
                  height: "48px",
                  border: `2px solid ${color.stroke}`,
                  backgroundColor: color.background,
                  borderRadius: "4px",
                  cursor: "pointer",
                  transition: "transform 0.1s",
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.transform = "scale(1.1)";
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.transform = "scale(1)";
                }}
                title={color.name}
                aria-label={`${color.name} sticky note`}
              />
            ))}
          </div>
        </Popover>
      )}
    </>
  );
};
